/**
 * tokens.ts — Design token primitives (SINGLE SOURCE OF TRUTH) + typed view.
 *
 * This is the ONLY place where raw color/radius/duration literals are allowed to live.
 * Everything else (Tailwind config, the MUI theme, CSS custom properties, and app code)
 * derives from here so the three styling systems can never drift apart.
 *
 * Authored as an ESM TypeScript module so it imports natively in the browser (Vite) with
 * zero CommonJS interop. `tailwind.config.js` — which runs in Node — loads it through the
 * already-bundled `jiti` transpiler (the same loader Tailwind uses for TS configs).
 *
 * Color values intentionally match Tailwind v3's default palette hexes, so migrating a
 * component from a raw utility (e.g. `bg-green-500`) to a semantic token (`bg-success-500`)
 * is pixel-identical. The canonical brand primary is blue-600 (#2563eb).
 */

export type ColorRamp = {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
};

/** Ordered ramp shades — the single source for every layer that iterates a ramp. */
export const RAMP_STEPS: readonly (keyof ColorRamp)[] = [
  50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950,
];

export type RampName = 'neutral' | 'primary' | 'success' | 'warning' | 'error' | 'danger' | 'info';

export type StatusKey =
  | 'completed'
  | 'preArrival'
  | 'preRegistered'
  | 'incomplete'
  | 'failed'
  | 'needsAttention'
  | 'default';

export interface ColorTokens {
  primary: string;
  primaryStrong: string;
  primaryHover: string;
  primarySoft: string;
  primaryRing: string;
  bg: string;
  surface: string;
  surfaceMuted: string;
  surfaceAlt: string;
  border: string;
  borderStrong: string;
  ink: string;
  inkBody: string;
  inkMuted: string;
  inkSubtle: string;
  white: string;
  black: string;
  tableHeader: string;
  tableRowAlt: string;
  tableRowHover: string;
}

export interface DesignTokens {
  ramp: Record<RampName, ColorRamp>;
  status: Record<StatusKey, ColorRamp>;
  chart: { success: string; warning: string; timing: string };
  color: ColorTokens;
  radii: Record<string, string>;
  transition: {
    duration: Record<string, string>;
    easing: Record<string, string>;
  };
  shadow: Record<string, string>;
}

// --- Neutral ramp (Tailwind `slate`) -------------------------------------------------
const slate: ColorRamp = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
  950: '#020617',
};

// --- Brand ramp (Tailwind `blue`) ----------------------------------------------------
const blue: ColorRamp = {
  50: '#eff6ff',
  100: '#dbeafe',
  200: '#bfdbfe',
  300: '#93c5fd',
  400: '#60a5fa',
  500: '#3b82f6',
  600: '#2563eb',
  700: '#1d4ed8',
  800: '#1e40af',
  900: '#1e3a8a',
  950: '#172554',
};

// --- Semantic source ramps (Tailwind defaults) ---------------------------------------
const green: ColorRamp = {
  50: '#f0fdf4',
  100: '#dcfce7',
  200: '#bbf7d0',
  300: '#86efac',
  400: '#4ade80',
  500: '#22c55e',
  600: '#16a34a',
  700: '#15803d',
  800: '#166534',
  900: '#14532d',
  950: '#052e16',
};

const amber: ColorRamp = {
  50: '#fffbeb',
  100: '#fef3c7',
  200: '#fde68a',
  300: '#fcd34d',
  400: '#fbbf24',
  500: '#f59e0b',
  600: '#d97706',
  700: '#b45309',
  800: '#92400e',
  900: '#78350f',
  950: '#451a03',
};

const red: ColorRamp = {
  50: '#fef2f2',
  100: '#fee2e2',
  200: '#fecaca',
  300: '#fca5a5',
  400: '#f87171',
  500: '#ef4444',
  600: '#dc2626',
  700: '#b91c1c',
  800: '#991b1b',
  900: '#7f1d1d',
  950: '#450a0a',
};

const sky: ColorRamp = {
  50: '#f0f9ff',
  100: '#e0f2fe',
  200: '#bae6fd',
  300: '#7dd3fc',
  400: '#38bdf8',
  500: '#0ea5e9',
  600: '#0284c7',
  700: '#0369a1',
  800: '#075985',
  900: '#0c4a6e',
  950: '#082f49',
};

const indigo: ColorRamp = {
  50: '#eef2ff',
  100: '#e0e7ff',
  200: '#c7d2fe',
  300: '#a5b4fc',
  400: '#818cf8',
  500: '#6366f1',
  600: '#4f46e5',
  700: '#4338ca',
  800: '#3730a3',
  900: '#312e81',
  950: '#1e1b4b',
};

// Decorative single-shade sources for the chart palette (not exposed as full ramps).
const emerald = { 600: '#059669' } as const;
const violet = { 600: '#7c3aed' } as const;

// --- Semantic color tokens -----------------------------------------------------------
// Named by MEANING, not hue, so a future re-skin only touches this file.
const colorTokens: ColorTokens = {
  // Brand
  primary: blue[600], // #2563eb — canonical brand blue
  primaryStrong: blue[700],
  primaryHover: blue[700],
  primarySoft: blue[50],
  primaryRing: blue[500],

  // Surfaces / ink / borders (migrated from the onboarding + index.css literals)
  bg: slate[50], // #f8fafc app background
  surface: '#ffffff',
  surfaceMuted: slate[50],
  surfaceAlt: blue[50],
  border: '#e5e7eb', // gray-200 — matches existing onboarding border
  borderStrong: '#d1d5db', // gray-300
  ink: '#111827', // gray-900 primary text
  inkBody: '#374151', // gray-700 body text
  inkMuted: '#6b7280', // gray-500
  inkSubtle: '#9ca3af', // gray-400
  white: '#ffffff',
  black: '#000000',

  // Table accents (migrated from index.css literals)
  tableHeader: '#dbe5f1',
  tableRowAlt: '#f8fbff',
  tableRowHover: '#f0f9ff',
};

export const tokens: DesignTokens = {
  // Full ramps, exposed under semantic names. `bg-success-500` === old `bg-green-500`.
  ramp: {
    neutral: slate,
    primary: blue,
    success: green,
    warning: amber,
    error: red,
    danger: red,
    info: sky,
  },

  // Status hues used by flow/reservation badges (mirrors FLOW_COLOR_MAP semantics).
  status: {
    completed: green,
    preArrival: indigo,
    preRegistered: sky,
    incomplete: amber,
    failed: red,
    needsAttention: red,
    default: slate,
  },

  // Chart palette (dashboard) — previously hardcoded hexes.
  chart: {
    success: emerald[600], // #059669
    warning: amber[500], // #f59e0b
    timing: violet[600], // #7c3aed
  },

  color: colorTokens,

  radii: {
    none: '0px',
    sm: '0.125rem',
    DEFAULT: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    full: '9999px',
  },

  transition: {
    duration: {
      fast: '150ms',
      normal: '250ms',
      slow: '350ms',
      bounce: '300ms',
    },
    easing: {
      out: 'ease-out',
      spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
      bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    },
  },

  shadow: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    card: '0 12px 24px -8px rgba(0, 0, 0, 0.15)',
  },
};

// Convenience semantic aliases for the most-used values.
export const ramp = tokens.ramp;
export const status = tokens.status;
export const color = tokens.color;
export const radii = tokens.radii;
export const transition = tokens.transition;
export const shadow = tokens.shadow;

export default tokens;
